<script setup>
/* eslint-env jquery */
import { ref, computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import TransportOrderDetailsGeneral from './TransportOrderDetailsGeneral.vue';
import TransportOrderDetailsAdditional from './TransportOrderDetailsAdditional.vue';
import TransportOrderActionsDetails from './TransportOrderActionsDetails.vue';
import { navItems } from '@/helpers/navigationPanelHelper.js';
import { useNavigationPanelStore } from '@/stores/navigationPanel';
import { useMapStore } from '../../stores/map';

const SHRINKED_LEFT_PANEL_WIDTH = '100px';
const STRETCHED_LEFT_PANEL_WIDTH = '25%';

const orderDetailsGeneralComponentKey = ref(0);
const orderDetailsAdditionalComponentKey = ref(0);
const orderDetailsActionsComponentKey = ref(0);
const isLoading = ref(false);
const order = ref(null);
const route = useRoute();
const navigationPanelStore = useNavigationPanelStore();
const generalDetailsCollapsed = ref(false);
const orderDetailsAdditional = ref();
const orderActionDetails = ref();
const mapStore = useMapStore();
const leftPanelWidth = ref(STRETCHED_LEFT_PANEL_WIDTH);

const orderIdForDetails = computed(() => {
  return parseInt(route.params.id);
});

watch(orderIdForDetails, () => {
  refreshAll();
});

// created
navigationPanelStore.setNavigationPanelItems(navItems.TRANSPORT_ORDER_DETAILS);

function collapseGeneralDetails() {
  toggleLayout();
  generalDetailsCollapsed.value = true;
}

function expandGeneralDetails() {
  toggleLayout();
}

function refreshAll() {
  orderDetailsGeneralComponentKey.value++;
  orderDetailsAdditionalComponentKey.value++;
  orderDetailsActionsComponentKey.value++;
}

function toggleLayout() {
  leftPanelWidth.value =
    leftPanelWidth.value === STRETCHED_LEFT_PANEL_WIDTH
      ? SHRINKED_LEFT_PANEL_WIDTH
      : STRETCHED_LEFT_PANEL_WIDTH;
}

function onLeftPanelTransitionEnd(event) {
  if (event.propertyName === 'flex-basis') {
    mapStore.tryToRefreshMaps();

    if (leftPanelWidth.value === STRETCHED_LEFT_PANEL_WIDTH) {
      generalDetailsCollapsed.value = false;
    }
  }
}
</script>

<template>
  <div>
    <div class="resizeable-container">
      <div
        class="left"
        :style="{ flexBasis: leftPanelWidth }"
        @transitionend="onLeftPanelTransitionEnd"
      >
        <TransportOrderDetailsGeneral
          :key="orderDetailsGeneralComponentKey"
          :id="orderIdForDetails"
          :collapsed="generalDetailsCollapsed"
          @refresh="orderDetailsGeneralComponentKey++"
          @onIsLoadingChanged="isLoading = $event"
          @onOrderLoaded="order = $event"
          @collapsePanel="collapseGeneralDetails"
          @expandPanel="expandGeneralDetails"
        />
      </div>
      <div v-if="order" class="right">
        <TransportOrderDetailsAdditional
          ref="orderDetailsAdditional"
          :key="orderDetailsAdditionalComponentKey"
          :id="orderIdForDetails"
          :isLoading="isLoading"
          :order="order"
          @refresh-general="orderDetailsGeneralComponentKey++"
          @refreshAll="refreshAll"
        />
        <TransportOrderActionsDetails
          ref="orderActionDetails"
          :key="orderDetailsActionsComponentKey"
          class="mt-4"
          :orderId="orderIdForDetails"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.resizeable-container {
  display: flex;
  gap: 25px;
}

.left {
  transition: flex-basis 1s ease;
}

.right {
  flex-grow: 1;
}
</style>
